import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Star } from "lucide-react";
import { heroLeftVariant } from "./shared";

const HERO_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/media/hero-video.mp4`;
const HERO_IMG = "https://images.pexels.com/videos/6150189/baby-baby-bump-baby-love-bebe-6150189.jpeg?auto=compress&cs=tinysrgb&w=1200";

const HeroSection = () => (
  <section data-testid="hero-section" className="relative min-h-screen flex items-center bg-emerald_deep pt-20 overflow-hidden">
    {/* Video background — full section, both mobile and desktop */}
    <div className="absolute inset-0 z-0">
      <video
        autoPlay muted loop playsInline
        poster={HERO_IMG}
        className="w-full h-full object-cover"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-emerald_deep/70"></div>
    </div>
    {/* Decorative dots */}
    <div className="absolute inset-0 opacity-[0.03] z-[1]" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 20%, #D4AF37 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
    <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center py-16 lg:py-0">
        <motion.div initial={heroLeftVariant.initial} animate={heroLeftVariant.animate} transition={heroLeftVariant.transition}>
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 px-4 py-1.5 rounded-full mb-8 backdrop-blur-sm">
            <Sparkles size={14} className="text-accent" />
            <span className="text-xs tracking-[0.15em] uppercase text-accent font-medium">1000+ Natural Pregnancies Achieved</span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-none tracking-tighter text-bone mb-8">
            Fertility &<br/>Hormonal <span className="italic font-light text-gold-gradient">Wellness,</span><br/>Elevated
          </h1>
          <p className="text-bone/60 text-lg leading-relaxed max-w-lg mb-10">
            Where medical excellence meets true nutrition science. Our expert team of doctors and nutritionists craft personalized guidance for your journey.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="/consultation" data-testid="hero-book-call-btn"
              className="bg-accent text-emerald_deep px-8 py-4 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all inline-flex items-center gap-3 rounded-full group">
              Book Consultation <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="/assessment" data-testid="hero-assessment-btn"
              className="border border-accent/30 text-accent px-8 py-4 text-sm tracking-wide hover:bg-accent/10 transition-all rounded-full inline-flex items-center gap-2">
              Take Free Assessment
            </a>
          </div>
          <div className="flex items-center gap-10 mt-16 pt-8 border-t border-accent/15">
            {[["10,000+", "Happy Clients"], ["15+", "Specialists"], ["98%", "Success Rate"]].map(([num, label]) => (
              <div key={label}>
                <div className="font-serif text-3xl text-accent tracking-tight">{num}</div>
                <div className="text-xs text-bone/40 uppercase tracking-[0.15em] mt-1">{label}</div>
              </div>
            ))}
          </div>
        </motion.div>
        {/* Right side — rating card overlay on desktop */}
        <div className="relative hidden lg:flex items-end justify-end pb-20">
          <div className="bg-emerald_deep/80 backdrop-blur-md rounded-2xl p-5 shadow-lg border border-accent/20">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={`star-${i}`} className="w-8 h-8 rounded-full bg-accent/20 border-2 border-emerald_deep flex items-center justify-center">
                    <Star size={10} className="text-accent fill-accent" />
                  </div>
                ))}
              </div>
              <div>
                <div className="font-serif text-lg text-accent">4.9 Rating</div>
                <div className="text-xs text-bone/50">114+ Google Reviews</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
